const KEY = 'tc_first_touch';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LEN = 200;

const SIGNAL_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
] as const;

export interface SignupAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  landing_path?: string;
  referrer_host?: string;
  first_seen_at?: string;
}

// C0 controls (U+0000-U+001F) and DEL (U+007F). A NUL from `?utm_source=%00` reached GoTrue,
// whose jsonb metadata column rejects U+0000, and failed every signup from that browser for 30 days.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\u0000-\u001F\u007F]/g;

function cleanString(val: unknown): string | undefined {
  if (typeof val !== 'string') return undefined;
  const trimmed = val.replace(CONTROL_CHARS_RE, '').trim().slice(0, MAX_VALUE_LEN);
  return trimmed.length > 0 ? trimmed : undefined;
}

function extractFirstPathSegment(pathname: string): string {
  const cleanPath = pathname.split(/[?#]/)[0] || '/';
  const firstSegment = cleanPath.split('/').filter(Boolean)[0]?.trim();
  if (!firstSegment) return '/';
  return `/${firstSegment}`.slice(0, MAX_VALUE_LEN);
}

function normalizeHost(host: string): string {
  return host.trim().toLowerCase().replace(/^www\./, '');
}

const REFERRER_DENYLIST_DOMAINS = [
  'stripe.com',
  'supabase.co',
  'supabase.in',
  'tradeconnector.com.au',
];

function isDenylistedHost(host: string): boolean {
  const lower = host.trim().toLowerCase();
  return REFERRER_DENYLIST_DOMAINS.some(
    (domain) => lower === domain || lower.endsWith(`.${domain}`)
  );
}

export function extractExternalReferrerHost(referrer: string, currentHostname: string): string | undefined {
  if (!referrer || !referrer.trim()) return undefined;
  try {
    const refUrl = new URL(referrer);
    const refHost = cleanString(refUrl.hostname);
    if (!refHost) return undefined;
    if (currentHostname && normalizeHost(refHost) === normalizeHost(currentHostname)) {
      return undefined;
    }
    if (isDenylistedHost(refHost)) {
      return undefined;
    }
    return refHost;
  } catch {
    return undefined;
  }
}

export function getSignupAttribution(): SignupAttribution | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return undefined;

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;

    const rec = parsed as Record<string, unknown>;
    const firstSeenRaw = cleanString(rec.first_seen_at);
    if (!firstSeenRaw) return undefined;

    const firstSeenMs = Date.parse(firstSeenRaw);
    if (Number.isNaN(firstSeenMs)) return undefined;

    if (Date.now() > firstSeenMs + TTL_MS) {
      localStorage.removeItem(KEY);
      return undefined;
    }

    const result: SignupAttribution = {};
    let hasSignal = false;
    for (const key of SIGNAL_PARAM_KEYS) {
      const val = cleanString(rec[key]);
      if (val !== undefined) {
        result[key] = val;
        hasSignal = true;
      }
    }

    const landingPath = cleanString(rec.landing_path);
    if (landingPath !== undefined) {
      result.landing_path = landingPath;
    }

    const referrerHost = cleanString(rec.referrer_host);
    if (referrerHost !== undefined) {
      result.referrer_host = referrerHost;
      hasSignal = true;
    }

    if (!hasSignal) return undefined;

    result.first_seen_at = firstSeenRaw;
    return result;
  } catch (err) {
    console.warn('[attributionStorage] Failed to read attribution:', err);
    return undefined;
  }
}

export function captureFirstTouchAttribution(
  searchParams?: URLSearchParams,
  pathname?: string
): void {
  if (typeof window === 'undefined') return;
  try {
    if (getSignupAttribution() !== undefined) return;

    const params = searchParams ?? new URLSearchParams(window.location.search);
    const record: SignupAttribution = {};
    let hasSignal = false;

    for (const key of SIGNAL_PARAM_KEYS) {
      const val = cleanString(params.get(key));
      if (val !== undefined) {
        record[key] = val;
        hasSignal = true;
      }
    }

    const referrerHost =
      typeof document !== 'undefined'
        ? extractExternalReferrerHost(document.referrer, window.location.hostname)
        : undefined;
    if (referrerHost !== undefined) {
      record.referrer_host = referrerHost;
      hasSignal = true;
    }

    if (!hasSignal) return;

    const targetPathname = pathname ?? window.location.pathname;
    record.landing_path = extractFirstPathSegment(targetPathname);
    record.first_seen_at = new Date(Date.now()).toISOString();

    localStorage.setItem(KEY, JSON.stringify(record));
  } catch (err) {
    console.warn('[attributionStorage] Failed to persist attribution:', err);
  }
}
